import { createFeatureSelector, createSelector } from '@ngrx/store';
import { environment } from '../../../../environments/environment';
import { NotificationTypeId } from '../../models/notification-type-id';
import {
  notificationPreferenceFeatureKey,
  NotificationPreferenceState,
} from '../reducer/notification-preference.reducer';

export const selectNotificationPreferenceFeature = createFeatureSelector<NotificationPreferenceState>(
  notificationPreferenceFeatureKey
);

/**
 * Pulls the validation messages out of a failure payload, or returns an empty array when it carries
 * none. Written against the shapes an ASP.NET validation response can arrive in — a string, a list
 * of messages, or a field-keyed map — because the field name is the whole point of the message here
 * and collapsing it to a constant is what used to throw it away.
 */
export function extractValidationMessages(error: unknown): string[] {
  if (typeof error === 'string') return error.trim() === '' ? [] : [error];
  if (Array.isArray(error)) return error.filter((m): m is string => typeof m === 'string' && m.trim() !== '');

  // Only array-valued properties are read. A field-keyed validation payload carries string[]; a
  // scalar property on some other error shape is not a message and must not be rendered as one.
  if (error !== null && typeof error === 'object') {
    return Object.values(error as Record<string, unknown>)
      .filter(Array.isArray)
      .flat()
      .filter((m): m is string => typeof m === 'string' && m.trim() !== '');
  }

  return [];
}

/**
 * Null only when there is no failure. `undefined` is a failure: the effects store
 * `err.error?.errors`, which is undefined for a 500 with a non-JSON body, a gateway error, a
 * timeout or an offline browser — all of which must still tell the user something.
 */
function toDisplayMessage(error: unknown): string | null {
  if (error === null) return null;

  const messages = extractValidationMessages(error);
  return messages.length > 0 ? messages.join(' ') : environment.errorMessage;
}

// Load failures. Kept separate from the save so a failed types re-fetch cannot render on the save
// surfaces as a rejection the user never caused.
export const selectError = createSelector(selectNotificationPreferenceFeature, state => toDisplayMessage(state.error));

// The API's no-clear rule rejects a save by naming the offending contact field, and that text is
// what reaches the user.
export const selectSaveError = createSelector(selectNotificationPreferenceFeature, state =>
  toDisplayMessage(state.saveError)
);

export const selectNotificationPreferences = createSelector(
  selectNotificationPreferenceFeature,
  state => state.notificationPreferences
);

export const selectIsSaving = createSelector(selectNotificationPreferenceFeature, state => state.isSaving);

export const selectIsLoading = createSelector(selectNotificationPreferenceFeature, state => state.isLoading);

export const selectNotificationTypes = createSelector(
  selectNotificationPreferenceFeature,
  state => state.notificationTypes?.filter(type => type.notificationTypeId !== NotificationTypeId.TextMessage) ?? null
);
